import { type FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, UserCog, UserPlus, Users } from 'lucide-react';

import { useAuth } from '@/shared/auth';
import { useDebouncedValue } from '@/shared/libs';
import {
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  ListToolbar,
  LoaderOverlay,
  PageHeader,
  Pagination,
  type PaginationPageSize,
  SimpleSelect,
} from '@/shared/ui';
import {
  isLastActiveSuperAdmin,
  isSelfUser,
  ROLE_LABEL_KEY,
  ROLE_OPTIONS_EXTERNAL,
  ROLE_OPTIONS_INTERNAL,
  USER_STATUS_FILTER_OPTIONS,
  USER_STATUS_I18N_KEY,
  useRegistrationInvites,
  useUsers,
} from '@entities/User';
import type { User, UserRole, UserStatus } from '@psp-aggregator/sdk';

import { UsersSubTab } from '../../model/users-sub-tab';
import { InvitationsTable } from '../InvitationsTable/InvitationsTable';
import { UsersManagementModals } from '../UsersManagementModals/UsersManagementModals';
import { UsersTable } from '../UsersTable/UsersTable';

export interface UsersManagementProps {
  onGoToDeals?: () => void;
  title?: string;
}

type UsersListSubTab = UsersSubTab.External | UsersSubTab.Internal;

const DEFAULT_ROLE_BY_TAB: Record<UsersListSubTab, UserRole> = {
  [UsersSubTab.External]: 'agent',
  [UsersSubTab.Internal]: 'super_admin',
};

const ROLE_OPTIONS_BY_TAB: Record<UsersListSubTab, readonly UserRole[]> = {
  [UsersSubTab.External]: ROLE_OPTIONS_EXTERNAL,
  [UsersSubTab.Internal]: ROLE_OPTIONS_INTERNAL,
};

const isUsersListSubTab = (tab: UsersSubTab): tab is UsersListSubTab =>
  tab === UsersSubTab.Internal || tab === UsersSubTab.External;

export const UsersManagement: FC<UsersManagementProps> = ({
  onGoToDeals,
  title,
}) => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [subTab, setSubTab] = useState<UsersSubTab>(UsersSubTab.Internal);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim());
  const [roleFilter, setRoleFilter] = useState<UserRole>(
    DEFAULT_ROLE_BY_TAB[UsersSubTab.Internal],
  );
  const [statusFilter, setStatusFilter] = useState<UserStatus | ''>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<PaginationPageSize>(20);
  const [assignTarget, setAssignTarget] = useState<User | null>(null);
  const [changeTarget, setChangeTarget] = useState<User | null>(null);
  const [rejectTarget, setRejectTarget] = useState<User | null>(null);
  const [detailUser, setDetailUser] = useState<User | null>(null);
  const [confirmBlock, setConfirmBlock] = useState<User | null>(null);
  const [confirmUnblock, setConfirmUnblock] = useState<User | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  useEffect(() => {
    setPage(1);

    if (isUsersListSubTab(subTab)) {
      setRoleFilter(DEFAULT_ROLE_BY_TAB[subTab]);
    }
  }, [subTab]);

  const usersQuery = useUsers({
    enabled: subTab !== UsersSubTab.Invitations,
    limit: pageSize,
    page,
    role: subTab === UsersSubTab.Invitations ? undefined : roleFilter,
    search: debouncedSearch || undefined,
    status:
      subTab === UsersSubTab.Invitations
        ? undefined
        : statusFilter || undefined,
  });
  const pendingQuery = useUsers({
    enabled: subTab === UsersSubTab.Invitations,
    limit: 50,
    page: 1,
    status: 'pending',
  });
  const invitesQuery = useRegistrationInvites({
    enabled: subTab === UsersSubTab.Invitations,
    limit: pageSize,
    page,
  });
  const superAdminCount = useUsers({ limit: 1, page: 1, role: 'super_admin' });
  const adminCount = useUsers({ limit: 1, page: 1, role: 'admin' });
  const agentCount = useUsers({ limit: 1, page: 1, role: 'agent' });
  const merchantCount = useUsers({ limit: 1, page: 1, role: 'merchant' });
  const activeSuperAdmins = useUsers({
    limit: 1,
    page: 1,
    role: 'super_admin',
    status: 'active',
  });

  const tabCounts = useMemo(
    () => ({
      external:
        (agentCount.data?.total ?? 0) + (merchantCount.data?.total ?? 0),
      internal:
        (superAdminCount.data?.total ?? 0) + (adminCount.data?.total ?? 0),
      invitations: invitesQuery.data?.total ?? 0,
    }),
    [
      adminCount.data?.total,
      agentCount.data?.total,
      invitesQuery.data?.total,
      merchantCount.data?.total,
      superAdminCount.data?.total,
    ],
  );

  const listTotal =
    subTab === UsersSubTab.Invitations
      ? (invitesQuery.data?.total ?? 0)
      : (usersQuery.data?.total ?? 0);
  const isLoading =
    subTab === UsersSubTab.Invitations
      ? invitesQuery.isLoading || pendingQuery.isLoading
      : usersQuery.isLoading;
  const isError =
    subTab === UsersSubTab.Invitations
      ? invitesQuery.isError || pendingQuery.isError
      : usersQuery.isError;
  const hasActiveFilters =
    isUsersListSubTab(subTab) &&
    (Boolean(search.trim()) ||
      Boolean(statusFilter) ||
      roleFilter !== DEFAULT_ROLE_BY_TAB[subTab]);
  const isListEmpty = !isLoading && listTotal === 0;

  const clearFilters = (): void => {
    setSearch('');
    setStatusFilter('');

    if (isUsersListSubTab(subTab)) {
      setRoleFilter(DEFAULT_ROLE_BY_TAB[subTab]);
    }

    setPage(1);
  };

  const subTabs = [
    {
      count: tabCounts.internal,
      icon: UserCog,
      key: UsersSubTab.Internal,
      label: t('pages.admin.users.subTabs.internal'),
    },
    {
      count: tabCounts.external,
      icon: Users,
      key: UsersSubTab.External,
      label: t('pages.admin.users.subTabs.external'),
    },
    {
      count: tabCounts.invitations,
      icon: Mail,
      key: UsersSubTab.Invitations,
      label: t('pages.admin.users.subTabs.invitations'),
    },
  ];

  if (isLoading) {
    return <LoaderOverlay show />;
  }

  if (isError) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>{t('errors.users.loadFailed')}</EmptyTitle>
          <EmptyDescription>{t('errors.unknown')}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div className="space-y-5">
      {!!title && (
        <PageHeader
          action={
            <Button
              size="sm"
              type="button"
              variant="brand"
              onClick={() => setInviteOpen(true)}>
              <UserPlus className="h-4 w-4" />
              {t('buttons.invite')}
            </Button>
          }
          title={title}
        />
      )}

      <ListToolbar
        count={
          subTab === UsersSubTab.Invitations
            ? (invitesQuery.data?.items.length ?? 0)
            : (usersQuery.data?.items.length ?? 0)
        }
        search={
          subTab === UsersSubTab.Invitations
            ? undefined
            : {
                onChange: (value) => {
                  setSearch(value);
                  setPage(1);
                },
                placeholder: t('pages.admin.users.searchPlaceholder'),
                value: search,
              }
        }
        total={listTotal}
      />

      {subTab !== UsersSubTab.Invitations && isUsersListSubTab(subTab) && (
        <div className="flex flex-wrap items-center gap-2">
          <SimpleSelect
            aria-label={t('pages.admin.users.filters.role')}
            options={ROLE_OPTIONS_BY_TAB[subTab].map((role) => ({
              label: t(ROLE_LABEL_KEY[role]),
              value: role,
            }))}
            value={roleFilter}
            onValueChange={(value) => {
              setRoleFilter(value as UserRole);
              setPage(1);
            }}
          />
          <SimpleSelect
            aria-label={t('pages.admin.users.filters.status')}
            options={[
              {
                label: t('pages.admin.users.allStatuses'),
                value: '',
              },
              ...USER_STATUS_FILTER_OPTIONS.map((status) => ({
                label: t(USER_STATUS_I18N_KEY[status]),
                value: status,
              })),
            ]}
            value={statusFilter}
            onValueChange={(value) => {
              setStatusFilter(value as UserStatus | '');
              setPage(1);
            }}
          />
        </div>
      )}

      <div className="w-full min-w-0 overflow-x-auto">
        <div className="inline-flex min-w-max rounded-xl bg-foreground/5 p-1">
          {subTabs.map((tab) => (
            <button
              key={tab.key}
              className={`inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-xs font-medium transition-colors ${
                subTab === tab.key
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              type="button"
              onClick={() => setSubTab(tab.key)}>
              <tab.icon className="h-3.5 w-3.5 shrink-0" />
              {tab.label}
              <span className="ml-1 rounded-full bg-foreground/10 px-1.5 py-0.5 text-[10px]">
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {subTab === UsersSubTab.Invitations ? (
        <div className="space-y-5">
          {(pendingQuery.data?.items.length ?? 0) > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t('pages.admin.users.awaitingRole')} ·{' '}
                {pendingQuery.data?.total ?? 0}
              </p>
              <UsersTable
                mode="pending"
                users={pendingQuery.data?.items ?? []}
                onAssign={setAssignTarget}
                onReject={setRejectTarget}
                onView={setDetailUser}
              />
            </div>
          )}
          <InvitationsTable invitations={invitesQuery.data?.items ?? []} />
        </div>
      ) : (
        <UsersTable
          filtered={hasActiveFilters}
          guardLastAdmin={(user) =>
            isLastActiveSuperAdmin(user, activeSuperAdmins.data?.total ?? 0)
          }
          guardSelf={(user) => isSelfUser(user, currentUser?.id)}
          mode={subTab}
          users={usersQuery.data?.items ?? []}
          onBlock={setConfirmBlock}
          onChangeRole={setChangeTarget}
          onClearFilters={clearFilters}
          onGoToDeals={onGoToDeals ? () => onGoToDeals() : undefined}
          onUnblock={setConfirmUnblock}
          onView={setDetailUser}
        />
      )}

      {!isListEmpty && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={listTotal}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      )}

      <UsersManagementModals
        assignTarget={assignTarget}
        changeTarget={changeTarget}
        confirmBlock={confirmBlock}
        confirmUnblock={confirmUnblock}
        detailUser={detailUser}
        inviteOpen={inviteOpen}
        rejectTarget={rejectTarget}
        onAssignClose={() => setAssignTarget(null)}
        onChangeClose={() => setChangeTarget(null)}
        onConfirmBlockClose={() => setConfirmBlock(null)}
        onConfirmUnblockClose={() => setConfirmUnblock(null)}
        onDetailClose={() => setDetailUser(null)}
        onInviteClose={() => setInviteOpen(false)}
        onRejectClose={() => setRejectTarget(null)}
      />
    </div>
  );
};

UsersManagement.displayName = 'UsersManagement';
